import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Sparkles, TrendingUp, BadgeCheck, Star, Loader2, CheckCircle2 } from "lucide-react";

const ICONS = { spotlight: Sparkles, featured: Star, hero: TrendingUp };

export default function Promote() {
  const { user } = useAuth();
  const [tiers, setTiers] = useState({});
  const [mine, setMine] = useState({});
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const loc = useLocation();
  const isSuccess = loc.pathname.endsWith("/success");
  const [pollTx, setPollTx] = useState(null);

  useEffect(() => {
    api.get("/promotions/tiers").then(r=>setTiers(r.data)).catch(()=>{});
    api.get("/promotions/mine").then(r=>setMine(r.data)).catch(()=>{});
  }, []);

  // Polling on /promote/success
  useEffect(() => {
    if (!isSuccess) return;
    const params = new URLSearchParams(loc.search);
    const sid = params.get("session_id");
    if (!sid) return;
    let alive = true; let tries = 0;
    const tick = async () => {
      if (!alive) return;
      tries += 1;
      try {
        const { data } = await api.get(`/promotions/status/${sid}`);
        setPollTx(data);
        if (data.payment_status === "paid" || tries >= 8) {
          if (data.payment_status === "paid") {
            const m = await api.get("/promotions/mine"); setMine(m.data);
          }
          return;
        }
      } catch (e) { setErr(formatErr(e)); return; }
      setTimeout(tick, 2000);
    };
    tick();
    return () => { alive = false; };
  }, [isSuccess, loc.search]);

  const buy = async (tierId) => {
    setBusy(tierId); setErr("");
    try {
      const { data } = await api.post("/promotions/checkout", { tier: tierId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { setErr(formatErr(e)); setBusy(""); }
  };

  if (isSuccess) {
    return (
      <div className="max-w-xl mx-auto px-6 py-20 text-center" data-testid="promote-success">
        {!pollTx && <Loader2 className="mx-auto animate-spin" size={32}/>}
        {pollTx?.payment_status === "paid" ? (
          <>
            <CheckCircle2 className="mx-auto text-[#0A0A0A]" size={48}/>
            <h1 className="h-display text-4xl mt-6">You're live.</h1>
            <p className="text-[#4A4A4A] mt-3">Your boost is active. We'll surface you across the feed and Discover.</p>
            <Link to="/feed" className="btn-primary mt-8 inline-flex">See the feed</Link>
          </>
        ) : (
          <p className="text-[#4A4A4A] mt-6">Confirming your promotion…</p>
        )}
      </div>
    );
  }

  const promotedUntil = mine?.promoted_until ? new Date(mine.promoted_until) : null;
  const isActive = promotedUntil && promotedUntil > new Date();

  return (
    <div className="max-w-6xl mx-auto px-6 sm:px-8 py-12" data-testid="promote-page">
      <p className="overline mb-3">Advertising</p>
      <h1 className="h-display text-5xl mb-3">Boost your Hooman.</h1>
      <p className="text-[#4A4A4A] max-w-xl mb-10">Pay to show up first in the feed and at the top of Discover. No bidding, no SEO games — just clear placements your customers can see.</p>

      {isActive && (
        <div className="card p-6 mb-8 bg-[#0A0A0A] text-white" data-testid="promote-active-banner">
          <div className="flex items-center gap-3">
            <BadgeCheck size={20}/>
            <div>
              <p className="font-heading text-xl">You're currently boosted — {tiers[mine.promo_tier]?.label || mine.promo_tier}</p>
              <p className="text-white/70 text-sm">Active until {promotedUntil.toLocaleDateString()}</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-5">
        {Object.entries(tiers).map(([id, t]) => {
          const Icon = ICONS[id] || Sparkles;
          const featured = id === "featured";
          return (
            <div key={id} className={`card p-7 flex flex-col ${featured ? "ring-2 ring-[#0A0A0A]" : ""}`} data-testid={`promo-tier-${id}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="h-12 w-12 rounded-full bg-[#F2F2F0] flex items-center justify-center"><Icon size={20}/></div>
                {featured && <span className="badge badge-verified">Best value</span>}
              </div>
              <p className="font-heading text-2xl">{t.label}</p>
              <p className="text-sm text-[#4A4A4A] mt-1">{t.blurb}</p>
              <div className="mt-6 flex items-baseline gap-1">
                <span className="font-heading text-4xl">${t.price}</span>
                <span className="text-[#8A8A8A] text-sm">/ {t.days}d</span>
              </div>
              <button onClick={()=>buy(id)} disabled={!!busy}
                className={`mt-6 ${featured ? "btn-primary" : "btn-secondary"} w-full`}
                data-testid={`promo-buy-${id}`}>
                {busy===id ? "Redirecting…" : "Promote me"}
              </button>
            </div>
          );
        })}
      </div>
      {err && <p className="text-sm text-[#B84B4B] mt-6" data-testid="promote-error">{err}</p>}

      <div className="card p-7 mt-12 grid md:grid-cols-3 gap-6 text-sm">
        <Why title="No bidding wars" body="Flat prices. Pay once, show up everywhere your customers look."/>
        <Why title="Clear 'Sponsored' tag" body="Visitors always see who paid for placement — trust stays intact."/>
        <Why title="Stops automatically" body="When your period ends, your placement quietly returns to organic."/>
      </div>
    </div>
  );
}

function Why({ title, body }) {
  return (
    <div>
      <p className="font-heading text-lg">{title}</p>
      <p className="text-[#4A4A4A] mt-2">{body}</p>
    </div>
  );
}
